import { Inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor } from '@angular/common/http';
import { Observable, tap, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import Swal from 'sweetalert2';

import { AuthService } from '../services/auth.service';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environments/environment';
import { isClientPermissionDenied } from 'src/app/core/permissions/permission-error.util';
import { PermissionActionService } from 'src/app/core/permissions/permission-action.service';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  private readonly loginRoute = '/auth/login';
  private readonly applicationAdminSelectionFlag =
    'applicationAdminCompanySelected';
  private readonly selectedCompanyKey = 'applicationAdminSelectedCompanyId';
  private readonly sessionUserIdentityKey = 'authSessionUserIdentity';
  private readonly integrationApiPrefix = '/api/Integration/';
  private readonly skipErrorToastHeader = 'x-cg-skip-error-toast';
  /** RefreshToken ile bir kez tekrar denendiğini işaretler — sonsuz döngüyü engeller. */
  private readonly refreshRetryHeader = 'x-cg-refresh-retried';
  /** Bu endpoint'lerin kendi 401'i asla refresh tetiklemez (döngüyü önler). */
  private readonly identityAuthMechanismPaths = [
    '/api/identity/login',
    '/api/identity/verifyotp',
    '/api/identity/resendotp',
    '/api/identity/refreshtoken',
    '/api/identity/logout',
  ];
  private readonly exemptAuthRoutes = [
    '/auth/login',
    '/auth/loginjtr',
    '/auth/sso-login',
    '/auth/sso-callback',
    '/auth/forgot-password',
    '/auth/forgot-success',
    '/auth/create-password',
    '/auth/confirm-email',
    '/auth/set-password',
    '/auth/change-email',
    '/auth/verify-otp',
    '/auth/logout',
  ];

  /**
   * @param {Router} _router
   * @param {AuthenticationService} _authenticationService
   */
  constructor(
    private _router: Router,
    private _authenticationService: AuthService,
    @Inject(Injector) private readonly injector: Injector,
  ) {}

  private get toast() {
    return this.injector.get(ToastrService);
  }

  private get modalService() {
    return this.injector.get(NgbModal);
  }

  private get permissionAction() {
    return this.injector.get(PermissionActionService);
  }

  private closeOpenOverlays(): void {
    if (this.modalService.hasOpenModals()) {
      this.modalService.dismissAll();
    }

    if (Swal.isVisible()) {
      Swal.close();
    }
  }

  private isExemptAuthRoute(url: string): boolean {
    const urlWithoutParams = (url || '').split('?')[0];
    return this.exemptAuthRoutes.some((route) =>
      urlWithoutParams.startsWith(route),
    );
  }

  private isCurrentLocationExempt(): boolean {
    const routerUrlExempt = this.isExemptAuthRoute(this._router.url);
    const browserPath = window?.location?.pathname || '';
    const browserUrlExempt = this.isExemptAuthRoute(browserPath);
    return routerUrlExempt || browserUrlExempt;
  }

  private isIdentityAuthMechanismRequest(url: string): boolean {
    const path = (url || '').split('?')[0].toLowerCase();
    return this.identityAuthMechanismPaths.some((p) => path.includes(p));
  }

  /**
   * 401 alan isteği, AuthService.refreshToken() ile access token'ı yeniledikten
   * sonra yeni token'la tekrar dener. Refresh de başarısız olursa (reuse detection
   * veya süresi dolmuş) AuthService zaten kullanıcıyı local olarak çıkış yaptırıp
   * login'e yönlendirir — burada tekrar deneme yapılmaz.
   */
  private retryWithRefreshedToken(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    return this._authenticationService.refreshToken().pipe(
      switchMap((updatedUser: any) => {
        const retriedRequest = request.clone({
          setHeaders: {
            Authorization: `Bearer ${updatedUser?.token}`,
            [this.refreshRetryHeader]: '1',
          },
        });
        return next.handle(retriedRequest);
      }),
      catchError((refreshErr) => throwError(() => refreshErr)),
    );
  }

  private redirectToLoginForSessionChange(): void {
    this.closeOpenOverlays();
    sessionStorage.removeItem(this.applicationAdminSelectionFlag);
    sessionStorage.removeItem(this.selectedCompanyKey);
    sessionStorage.removeItem(this.sessionUserIdentityKey);
    this._authenticationService.currentUserValue = undefined;

    if (!this._router.url.startsWith(this.loginRoute)) {
      this._router.navigate([this.loginRoute], {
        queryParams: { reason: 'session-changed' },
      });
    }
  }

  intercept(
    request: HttpRequest<any>,
    next: HttpHandler,
  ): Observable<HttpEvent<any>> {
    const isIntegrationApiRequest = request.url.includes(
      this.integrationApiPrefix,
    );
    const shouldSkipErrorToast =
      request.headers.get(this.skipErrorToastHeader) === '1';

    return next.handle(request).pipe(
      catchError((err) => {
        if (!environment.production) console.log('HTTP Error:', err.status);
        const isDownloadDocumentRequest = request.url.includes(
          '/api/Emission/DownloadDocument/',
        );

        // DownloadDocument hatalarini component seviyesinde handle ediyoruz.
        // HttpErrorResponse yapisini bozmadan oldugu gibi iletmeliyiz.
        if (isDownloadDocumentRequest) {
          return throwError(() => err);
        }

        // Safely extract error message
        let error =
          err.error?.message ||
          err.error ||
          err.statusText ||
          'An error occurred';
        if (typeof error === 'object') {
          error = err.error?.message || err.message || 'An error occurred';
        }

        // Handle 423 Locked status
        if (err.status === 423) {
          const lockMessage =
            err.error?.message ||
            err.error ||
            'Bu işlem şu anda yapılamıyor. Kayıt kilitli veya başka bir kullanıcı tarafından kullanılıyor.';
          if (!shouldSkipErrorToast) {
            this.toast.warning(lockMessage, 'Uyarı!', {
              progressBar: true,
              toastClass: 'ngx-toastr',
              progressAnimation: 'decreasing',
              closeButton: true,
              timeOut: 5000,
            });
          }
          return throwError(() => ({ message: lockMessage, status: 423 }));
        }

        if (err.status >= 200 && err.status < 300) {
          //return;
        } else if (err.status === 401) {
          const isAuthMechanismRequest = this.isIdentityAuthMechanismRequest(
            request.url,
          );
          const alreadyRetried = request.headers.has(this.refreshRetryHeader);
          const hasRefreshToken = !!this._authenticationService
            .currentUserValue?.refreshToken;

          const canAttemptRefresh =
            !isAuthMechanismRequest &&
            !alreadyRetried &&
            hasRefreshToken &&
            !this.isCurrentLocationExempt();

          if (canAttemptRefresh) {
            return this.retryWithRefreshedToken(request, next);
          }

          // A stale tab can send unauthenticated requests after context drift.
          // On create/reset password pages we must preserve the current auth flow.
          if (!this.isCurrentLocationExempt()) {
            this.redirectToLoginForSessionChange();
          }
        } else if (err.status === 403) {
          // FE permission interceptor zaten Swal gösterdi — sayfada kal
          if (isClientPermissionDenied(err)) {
            return throwError(() => err);
          }
          // Integration test endpoints should return the 403 to UI,
          // so panel can render forbidden status/result payload.
          if (isIntegrationApiRequest) {
            return throwError(() => err);
          }
          // Backend 403: 404/not-authorized'a gitme — "Yetkiniz Bulunmamaktadır"
          this.permissionAction.showDenied();
          return throwError(() => err);
        }

        if (err.status >= 400 && err.status < 500 && err.status !== 401) {
          if (!environment.production) console.log('Client error:', err.status);
          if (!shouldSkipErrorToast && !isClientPermissionDenied(err)) {
            this.toast.error(error, 'Error!', {
              progressBar: true,
              toastClass: 'ngx-toastr',
              progressAnimation: 'decreasing',
              closeButton: true,
              timeOut: 5000,
            });
          }
        }

        return throwError(() => err);
      }),
    );
  }
}
